<script setup>
import { ref, computed, onMounted } from "vue";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useExamFetchStore } from "@/stores/exam/exam-fetch-store";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";

const examStore = useExamFetchStore();
const authStore = useAuthStore();
const sharedStore = useSharedStore();
const certComponent = ref(null);
const selectedCertificate = ref(null);
const currentPage = ref(1);
const perPage = 10;

const totalPages = computed(() =>
  Math.ceil(examStore.myCertificatesList.length / perPage)
);

const paginatedCertificates = computed(() => {
  const start = (currentPage.value - 1) * perPage;
  return examStore.myCertificatesList.slice(start, start + perPage);
});

const hasCertificates = computed(
  () => examStore.myCertificatesList.length > 0
);

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const viewCertificate = (item) => {
  if (!item) return;
  const pdfPath = typeof item === "string" ? item : (item.pdfFile || item.pdf_file);
  if (!pdfPath) return;
  const url = pdfPath.startsWith("http")
    ? pdfPath
    : `${authStore.Url}${pdfPath}`;
  window.open(url, "_blank");
};

onMounted(() => {
  examStore.fetchMyCertificates();
});

const downloadPDF = async () => {
  if (!certComponent.value?.certRef) return;

  const element = certComponent.value.certRef;

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
  });

  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF({
    orientation: canvas.width > canvas.height ? "landscape" : "portrait",
    unit: "px",
    format: [canvas.width, canvas.height],
  });

  pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
  pdf.save(`certificate-${Date.now()}.pdf`);
};
</script>

<template>
  <layouts-header></layouts-header>
  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">My Certificates</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                My Certificates
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <div class="content">
    <div class="container">
      <!-- Profile -->
      <student-header></student-header>
      <!-- /Profile -->

      <div class="row">
        <!-- Sidebar -->
        <student-sidebar></student-sidebar>
        <!-- sidebar -->
        <div class="col-lg-9">
          <div
            class="page-title d-flex align-items-center justify-content-between"
          >
            <h5>My Certificates</h5>
          </div>

          <div
            v-if="!hasCertificates"
            class="text-center p-5 bg-light border rounded mb-3"
          >
            <h5 class="mb-2">Belum ada sertifikat</h5>
            <p class="mb-3 text-muted">
              Sertifikat akan muncul di sini setelah Anda menyelesaikan course
              dan lulus exam.
            </p>
            <router-link to="/course/course-grid" class="btn btn-primary">
              Cari Course
            </router-link>
          </div>

          <template v-else>
            <div class="table-responsive custom-table">
              <table class="table">
                <thead class="thead-light">
                  <tr>
                    <th>No.</th>
                    <th>Course Name</th>
                    <th>Date</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in paginatedCertificates"
                    :key="item.id || index"
                  >
                    <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
                    <td>
                      <div class="fw-semibold">
                        {{ item.courseName }}
                      </div>
                    </td>
                    <td>{{ sharedStore.formatTanggal(item.issuedDate) }}</td>
                    <td>
                      <div class="d-flex align-items-center">
                        <a
                          href="javascript:void(0)"
                          class="d-inline-flex fs-14 me-1 action-icon"
                          title="Buka Sertifikat di Tab Baru"
                          @click.prevent="viewCertificate(item.pdfFile)"
                          ><i class="isax isax-eye"></i
                        ></a>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="row align-items-center mt-3" v-if="totalPages > 1">
              <div class="col-md-2">
                <p class="pagination-text">
                  Page {{ currentPage }} of {{ totalPages }}
                </p>
              </div>
              <div class="col-md-10">
                <ul
                  class="pagination lms-page justify-content-center justify-content-md-end mt-2 mt-md-0"
                >
                  <li
                    class="page-item prev"
                    :class="{ disabled: currentPage === 1 }"
                  >
                    <a
                      class="page-link"
                      href="javascript:void(0)"
                      tabindex="-1"
                      @click="goToPage(currentPage - 1)"
                      ><i class="fas fa-angle-left"></i
                    ></a>
                  </li>
                  <li
                    class="page-item"
                    v-for="page in totalPages"
                    :key="page"
                    :class="{ active: currentPage === page }"
                  >
                    <a
                      class="page-link"
                      href="javascript:void(0)"
                      @click="goToPage(page)"
                    >
                      {{ page }}</a
                    >
                  </li>
                  <li
                    class="page-item next"
                    :class="{ disabled: currentPage === totalPages }"
                  >
                    <a
                      class="page-link"
                      href="javascript:void(0)"
                      @click="goToPage(currentPage + 1)"
                      ><i class="fas fa-angle-right"></i
                    ></a>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>

  <!-- View Certificate -->
  <div class="modal fade" id="view_certificate">
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5>View Certificate</h5>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="isax isax-close-circle5"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="d-flex justify-content-center align-items-center">
            <iframe
              v-if="selectedCertificate"
              :src="selectedCertificate"
              width="100%"
              height="100%"
              style="border: none; min-height: 85vh"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- /View Certificate -->
</template>
